import { useCallback } from 'react';
import { MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { useQuery } from '@apollo/client';
import { GET_CONTACTS_BY_CHANNEL_QUERY, type GetContactsByChannelOutput } from 'graphql/webuser/WebUserQueries';
import { useSelectedLinkContext , useWebUserContext } from 'contexts';
import type { WebUserContact } from 'types/User';
import { getUserFullName } from 'utils/webUser';
import { StateSetter } from 'types/utils/React';
import { ContactAvatar } from 'components';
import useStyles from './FolderContactsSelect.styles';
import SelectedContactLabels from './FolderContactsSelect/SelectedContactLabels';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

type FolderContactsSelectPureProps = {
  channelContacts: WebUserContact[];
  contacts: string[];
  handleContactsChange: (event: SelectChangeEvent<string[]>) => void;
};

export const FolderContactsSelectPure = ({
  channelContacts,
  contacts,
  handleContactsChange,
}: FolderContactsSelectPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <Select
      className={classes.select}
      data-testid="folder-contacts-select"
      displayEmpty
      multiple
      name='contacts'
      value={contacts}
      onChange={handleContactsChange}
      renderValue={(selectedContactIds: string[]) => (
        <SelectedContactLabels
          channelContacts={channelContacts}
          selectedContacts={selectedContactIds}
        />
      )}
      MenuProps={MenuProps}
    >
      {channelContacts.map((contact) => (
        <MenuItem key={contact.id} value={contact.id}>
          <ContactAvatar className={classes.avatar} contact={contact} />
          {getUserFullName(contact)}
        </MenuItem>
      ))}
    </Select>
  );
};

type FolderContactsSelectProps = {
  contacts: string[];
  setContacts: StateSetter<string[]>;
};

const FolderContactsSelect = ({
  contacts,
  setContacts,
}: FolderContactsSelectProps): JSX.Element | null => {
  const { selectedLink } = useSelectedLinkContext();
  const { webUser } = useWebUserContext();

  const { data } = useQuery<GetContactsByChannelOutput>(
    GET_CONTACTS_BY_CHANNEL_QUERY,
    {
      variables: {
        channel: selectedLink?.channelKey,
      },
    },
  );

  const handleContactsChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;

      if (Array.isArray(value)) {
        setContacts(value);
      } else {
        setContacts([value]);
      }
    },
    [setContacts],
  );

  if (data?.webUsers?.errorReason) {
    return (
      <div>
        Erreur de récupération des contacts. Veuillez réessayer ultérieurement.
      </div>
    );
  }

  const channelContacts = data?.webUsers?.webUsers;

  if (!channelContacts) {
    return null;
  }

  const channelContactsWithoutMe = channelContacts.filter(
    (_) => _.id !== webUser.id,
  );

  return (
    <FolderContactsSelectPure
      channelContacts={channelContactsWithoutMe}
      contacts={contacts}
      handleContactsChange={handleContactsChange}
    />
  );
};

export default FolderContactsSelect;
